import React, { useState, useEffect } from 'react';
import './App.css';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';

function Account() {
  const { user, signOut, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('books');
  const [userBooks, setUserBooks] = useState([]);
  const [userConsultations, setUserConsultations] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);
  const [loadingConsultations, setLoadingConsultations] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Helper to check if mobile view
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 480px)').matches;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchUserBooks();
    fetchUserConsultations();
  }, [user, navigate]);

  const fetchUserBooks = async () => {
    if (!user?.email) return;
    
    try {
      setLoadingBooks(true);
      const { data, error } = await supabase
        .from('book_orders')
        .select('*')
        .eq('email', user.email)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Group orders by order_id
      const groupedOrders = {};
      if (data) {
        data.forEach(order => {
          if (!groupedOrders[order.order_id]) {
            groupedOrders[order.order_id] = {
              orderId: order.order_id,
              date: new Date(order.created_at).toLocaleDateString(),
              total: `$${order.total_amount?.toFixed(2) || order.total_amount || '0.00'}`,
              status: order.status || 'pending',
              items: []
            };
          }
          groupedOrders[order.order_id].items.push({
            title: order.book_title || order.product_name,
            quantity: order.quantity || 1,
            price: order.unit_price || order.price
          });
        });
      }
      
      setUserBooks(Object.values(groupedOrders));
    } catch (error) {
      console.error('Error fetching user books:', error);
      setUserBooks([]);
    } finally {
      setLoadingBooks(false);
    }
  };

  const fetchUserConsultations = async () => {
    if (!user?.email) return;
    
    try {
      setLoadingConsultations(true);
      const { data, error } = await supabase
        .from('consultations')
        .select('*')
        .eq('email', user.email)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setUserConsultations(data || []);
    } catch (error) {
      console.error('Error fetching user consultations:', error);
      setUserConsultations([]);
    } finally {
      setLoadingConsultations(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const { error } = await deleteAccount();
      if (error) {
        alert('Error deleting account: ' + error.message);
        setDeleting(false);
        return;
      }
      // Account deleted, redirect to home
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const renderBooks = () => (
    <div className="account-section">
      <h3>My Book Orders</h3>
      {loadingBooks ? (
        <p>Loading your orders...</p>
      ) : userBooks.length === 0 ? (
        <div className="no-orders">
          <p>You haven't ordered any books yet.</p>
          <Link to="/books" className="auth-link">Browse Books</Link>
        </div>
      ) : (
        <div className="orders-table">
          <div className="order-header">
            <span>Order ID</span>
            <span>Date</span>
            <span>Items</span>
            <span>Total</span>
            <span>Status</span>
          </div>
          {userBooks.map((order, index) => (
            <div key={index} className="order-row">
              <span>{order.orderId}</span>
              <span>{order.date}</span>
              <span className="order-items">
                {order.items?.map((item, idx) => (
                  <div key={idx}>
                    {item.quantity}x {item.title} (${item.price} each)
                  </div>
                ))}
              </span>
              <span>{order.total}</span>
              <span className={`order-status ${order.status.toLowerCase()}`}>
                {order.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderConsultations = () => (
    <div className="account-section">
      <h3>My Consultations</h3>
      {loadingConsultations ? (
        <p>Loading your consultations...</p>
      ) : userConsultations.length === 0 ? (
        <div className="no-orders">
          <p>You haven't booked any consultations yet.</p>
          <Link to="/consultations" className="auth-link">Book a Consultation</Link>
        </div>
      ) : (
        <div className="bookings-list">
          {userConsultations.map((consultation) => (
            <div key={consultation.id} className={`booking-item ${consultation.status.toLowerCase()}`}>
              <div className="booking-info">
                <h4>{consultation.session_type}</h4>
                <p>Date: {new Date(consultation.session_date).toLocaleDateString()} at {consultation.session_time}</p>
                <p>Timezone: {consultation.timezone}</p>
                {consultation.questions && (
                  <p className="consultation-questions">Questions: {consultation.questions}</p>
                )}
                <span className={`booking-status ${consultation.status.toLowerCase()}`}>
                  {consultation.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="main-bg">
      {/* Hide hero-section on mobile */}
      {!isMobile && (
        <header className="hero-section">
          <h1 className="site-title">My Account</h1>
          <p className="site-tagline">Manage your purchases, bookings, downloads, and preferences.</p>
        </header>
      )}
      <div className="account-container">
        <div className="account-tabs">
          <button 
            className={`account-tab ${activeTab === 'books' ? 'active' : ''}`}
            onClick={() => setActiveTab('books')}
          >
            📚 My Books
          </button>
          <button 
            className={`account-tab ${activeTab === 'consultations' ? 'active' : ''}`}
            onClick={() => setActiveTab('consultations')}
          >
            📅 My Consultations
          </button>
        </div>
        <div className="account-content">
          {activeTab === 'books' && renderBooks()}
          {activeTab === 'consultations' && renderConsultations()}
        </div>
        <div className="account-actions">
          <button 
            className="delete-account-btn" 
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Account
          </button>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="delete-account-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-account-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account</h3>
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            <p className="delete-warning">You will not be able to login again after deletion.</p>
            <div className="delete-modal-actions">
              <button 
                className="cancel-delete-btn" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Life Force Dynamics. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Account; 